import { lazy, Suspense } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../ui/sheet";
import {
  Home, Plus, MessageCircle, BookOpen,
  PanelLeftClose, PanelLeft, Coins, Brain,
} from "lucide-react";
import AdminMenu from "../AdminMenu";
import RelationshipBrainSheet from "../relationships/RelationshipBrainSheet";

// Coin pill — clicks navigate to /plans?tab=topup and pass the current URL
// as state.from so the plans page's back arrow returns the user to the exact
// /eroscoach/:matchId route they left (preserving match + session selection).
// URL is now kept in sync by ErosCoach's mirror effects, so window.location
// is authoritative — no more window.__erosCoachSessionId__ side-channel.
function CoinBalanceLink({ coinBalance }) {
  const navigate = useNavigate();
  const location = useLocation();
  const goTopup = () => {
    const from = `${location.pathname}${location.search}${location.hash}`;
    navigate("/plans?tab=topup", { state: { from } });
  };
  return (
    <div
      onClick={goTopup}
      className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full cursor-pointer hover:bg-amber-500/20 transition-colors"
      title="Top up coins"
      data-testid="coin-balance-display"
    >
      <Coins className="w-4 h-4 text-amber-400" />
      <span className="text-amber-400 font-medium text-sm">
        {coinBalance !== null ? coinBalance.toLocaleString() : "—"}
      </span>
    </div>
  );
}

// Lazy-loaded: ~450 LOC of prompt library data only fetches when the user
// opens the Prompt Library sheet (BookOpen icon). Keeps the initial
// ErosCoach bundle leaner.
const CoachPromptLibrarySheetContent = lazy(
  () => import("./CoachPromptLibrarySheetContent")
);

/**
 * Header of the ErosCoach page — sidebar toggles, match selector, coin
 * balance, Brain Sheet (perspective panel), Prompt Library Sheet, new
 * chat, refresh. Extracted from `pages/ErosCoach.jsx` as part of the
 * P1 #2 refactor (pure code-move, zero behaviour change).
 *
 * All state lives in the parent `ErosCoach`. Handlers passed via props.
 */
export default function CoachHeader({
  // Sidebar toggles
  sidebarOpen,
  setSidebarOpen,
  setMobileSidebarOpen,
  // Navigation
  navigate,
  // Match selector / search
  matches,
  selectedMatch,
  setSelectedMatch,
  setCurrentSessionId,
  setMessages,
  setPendingMatchSelection,
  isSearchActive,
  searchResults,
  restoreSearchResults,
  clearSearch,
  // Coins
  coinBalance,
  setShowCoinPurchaseModal,
  // Perspective sheet
  perspectiveOpen,
  setPerspectiveOpen,
  perspectiveRefreshKey,
  setPerspectiveRefreshKey,
  hasPendingExploration,
  explorationAcknowledged,
  pendingBioUpdatesCount,
  brainTab,
  setBrainTab,
  currentMatchObject,
  currentSessionId,
  userPersona,
  // Prompt library
  settingsOpen,
  setSettingsOpen,
  promptLibraryStatus,
  setPromptLibraryStatus,
  handlePromptSelect,
  // Top-right buttons
  createNewSession,
  fetchSessions,
}) {
  return (
    <header className="fixed md:sticky top-0 left-0 right-0 z-50 backdrop-blur-xl border-b bg-divine-charcoal/95 border-divine-gold/20">
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Sidebar Toggle - Desktop */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden md:flex text-white/60 hover:text-white"
            title={sidebarOpen ? "Close sessions panel" : "Open sessions panel"}
            data-testid="toggle-sidebar-btn"
          >
            {sidebarOpen ? <PanelLeftClose className="w-5 h-5" /> : <PanelLeft className="w-5 h-5" />}
          </Button>

          {/* Mobile menu button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileSidebarOpen(true)}
            className="md:hidden text-white/60 hover:text-white"
            data-testid="mobile-menu-btn"
          >
            <PanelLeft className="w-5 h-5" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/dashboard")}
            className="text-white/60 hover:text-white"
            data-testid="coach-home-btn"
            title="Home"
          >
            <Home className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <MessageCircle className="w-6 h-6 text-divine-gold hidden sm:block" />
            <h1 className="text-xl font-semibold font-cinzel text-divine-gold hidden sm:block">
              Agent Eros Advice
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Match Selector with persisted search results */}
          <Select
            value={isSearchActive ? "search" : (selectedMatch || "none")}
            onValueChange={(v) => {
              if (v === "search") {
                restoreSearchResults();
                return;
              }
              clearSearch();
              const newMatch = v === "none" ? null : v;
              // Dropdown-initiated match change: clear chat + require an
              // explicit session pick so we can't carry a stale
              // currentSessionId into the new match's effects.
              if (newMatch !== selectedMatch) {
                setCurrentSessionId(null);
                setMessages([]);
                setPendingMatchSelection(true);
              }
              setSelectedMatch(newMatch);
            }}
          >
            <SelectTrigger className="w-32 sm:w-40 bg-white/5 border-divine-gold/30">
              <SelectValue placeholder="Select match" />
            </SelectTrigger>
            <SelectContent>
              {/* Show saved search results option if we have results */}
              {searchResults.length > 0 && (
                <SelectItem value="search">
                  Search Results ({searchResults.length})
                </SelectItem>
              )}
              <SelectItem value="none">General Advice</SelectItem>
              {matches.map(m => (
                <SelectItem key={m.match_id} value={m.match_id}>
                  {m.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Coin Balance Display — routes to /plans?tab=topup, remembering
              the current /eroscoach URL so the plans Back button drops the
              user back into the exact match/session they left. */}
          <CoinBalanceLink coinBalance={coinBalance} />

          {/* Settings */}
          <AdminMenu />
          {/* Perspective Panel — Phase 3 read-only view (Memory Plan v1) */}
          <Sheet
            open={perspectiveOpen}
            onOpenChange={(open) => {
              setPerspectiveOpen(open);
              if (open) {
                // Deep-link to the Stage tab when a red-dot exploration is
                // pending — that's where both explore CTAs live. Otherwise
                // keep the user on whatever tab they last chose.
                if (hasPendingExploration && !explorationAcknowledged) {
                  setBrainTab("stage");
                }
                // Re-fetch perspective every time the sheet opens so digest
                // re-runs in another tab/window land here without a page reload.
                setPerspectiveRefreshKey((k) => k + 1);
              }
            }}
          >
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-white/60 hover:text-white relative"
                title={
                  selectedMatch
                    ? (() => {
                        const parts = [];
                        if (hasPendingExploration && !explorationAcknowledged) {
                          parts.push("Eros has spotted a coaching gap — open to explore");
                        }
                        if (pendingBioUpdatesCount > 0) {
                          parts.push(
                            `${pendingBioUpdatesCount} new bio update${pendingBioUpdatesCount === 1 ? "" : "s"} waiting for review`
                          );
                        }
                        return parts.length
                          ? parts.join(" · ")
                          : "View Eros's perspective on this relationship";
                      })()
                    : "Select a match to view perspective"
                }
                disabled={!selectedMatch}
                data-testid="open-perspective-btn"
              >
                <Brain className="w-5 h-5" />
                {hasPendingExploration && !explorationAcknowledged ? (
                  <span
                    data-testid="perspective-pending-dot"
                    aria-label="Coaching gap detected — open to explore"
                    className="absolute top-1 right-1 inline-block w-2 h-2 rounded-full bg-rose-500 ring-2 ring-divine-charcoal animate-pulse"
                  />
                ) : null}
                {pendingBioUpdatesCount > 0 ? (
                  <span
                    data-testid="bio-updates-pending-dot"
                    aria-label={
                      `${pendingBioUpdatesCount} new bio update${pendingBioUpdatesCount === 1 ? "" : "s"} waiting for review`
                    }
                    className="absolute top-1 left-1 inline-block w-2 h-2 rounded-full bg-sky-400 ring-2 ring-divine-charcoal animate-pulse"
                  />
                ) : null}
              </Button>
            </SheetTrigger>
            <SheetContent className="bg-divine-charcoal border-divine-gold/20 overflow-y-auto w-full sm:max-w-md">
              <SheetHeader>
                <SheetTitle className="font-cinzel text-divine-gold">
                  Relationship Perspective
                </SheetTitle>
              </SheetHeader>
              <div className="mt-6">
                <RelationshipBrainSheet
                  matchId={selectedMatch}
                  matchName={currentMatchObject?.name}
                  userPersonaId={userPersona?.match_id}
                  userPersonaName={userPersona?.name}
                  refreshKey={perspectiveRefreshKey}
                  coachSessionId={currentSessionId}
                  value={brainTab}
                  onValueChange={setBrainTab}
                />
              </div>
            </SheetContent>
          </Sheet>
          <Sheet open={settingsOpen} onOpenChange={setSettingsOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-white/60 hover:text-white">
                <BookOpen className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent className="bg-divine-charcoal border-divine-gold/20 overflow-y-auto">
              <SheetHeader>
                <SheetTitle className="font-cinzel text-divine-gold">Prompt Library</SheetTitle>
              </SheetHeader>
              {/* Lazy-mount the prompt library body only after the sheet
                  has been opened at least once. `settingsOpen` is the
                  Sheet's own controlled-state — first open triggers the
                  dynamic import; subsequent close/reopen keeps the
                  resolved chunk in cache. */}
              {settingsOpen && (
                <Suspense
                  fallback={
                    <div className="mt-6 text-white/40 text-sm">Loading prompts…</div>
                  }
                >
                  <CoachPromptLibrarySheetContent
                    promptLibraryStatus={promptLibraryStatus}
                    setPromptLibraryStatus={setPromptLibraryStatus}
                    handlePromptSelect={handlePromptSelect}
                    currentMatchObject={currentMatchObject}
                  />
                </Suspense>
              )}
            </SheetContent>
          </Sheet>

          {/* New Chat */}
          <Button
            variant="ghost"
            size="icon"
            onClick={createNewSession}
            className="text-white/60 hover:text-white"
          >
            <Plus className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </header>
  );
}
